import React, { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';

/**
 * Replaces the previous chat-thread UI with a simple "send Ethan a
 * message" form for students. The message goes to /messages/authed
 * which writes a Message row + emails the tutor; Ethan replies in
 * whatever channel he prefers (usually email).
 *
 * Tutors landing here are redirected to /admin where their incoming
 * messages list lives — they don't need a thread UI to reply, the
 * message arrives by email and they reply by email.
 */
const ChatPage: React.FC = () => {
  const { user } = useAuth();
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Track recently-sent so the user sees confirmation before
  // reset-on-fade. Auto-resets to fresh form after a few seconds so
  // they can send another.
  useEffect(() => {
    if (!sent) return;
    const t = setTimeout(() => setSent(false), 5000);
    return () => clearTimeout(t);
  }, [sent]);

  if (user?.role === 'TUTOR') {
    return <Navigate to="/admin" replace />;
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (message.trim().length === 0) {
      setError('Write a message before sending.');
      return;
    }
    setBusy(true);
    try {
      await api.post('/messages/authed', { message: message.trim() });
      setMessage('');
      setSent(true);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.response?.data?.message || 'Could not send. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const firstName = user?.name.split(' ')[0] || user?.name || '';

  return (
    <div className="bg-cream min-h-[60vh]">
      <Helmet>
        <title>Message Ethan — EJL Education</title>
      </Helmet>
      <section className="section">
        <div className="container-content max-w-[60ch]">
          <h1 className="mb-4">Message Ethan</h1>
          <p className="text-body text-ink/90 mb-6">
            {firstName ? `Hi ${firstName} — ` : ''}anything you want to flag — scheduling, a question about a lesson, something
            outside the usual flow. Goes straight to Ethan's inbox; he'll reply by email.
          </p>

          {sent && (
            <div className="border border-rule bg-paper p-4 mb-6">
              <p className="text-ink text-[15px]">
                Sent. Ethan will reply by email within 24 hours.
              </p>
            </div>
          )}

          {error && (
            <div className="border border-rule bg-paper p-4 mb-6">
              <p className="text-ink text-[15px]">{error}</p>
            </div>
          )}

          <form onSubmit={submit} noValidate className="space-y-6">
            <div>
              <label htmlFor="message" className="block text-[15px] font-medium text-ink mb-2">
                Your message
              </label>
              <textarea
                id="message"
                rows={8}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="input"
                placeholder="What's on your mind?"
                required
              />
            </div>

            <div className="flex justify-between items-center pt-2">
              <Link to="/dashboard" className="text-sm text-muted hover:text-ochre">
                Back to dashboard
              </Link>
              <button
                type="submit"
                disabled={busy}
                className="btn btn-primary disabled:opacity-50"
              >
                {busy ? 'Sending…' : 'Send message'}
              </button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
};

export default ChatPage;
